import React, { useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { X, CheckCircle, Gauge, AlertCircle, Upload, FileText, Trash2 } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/** Normalize any axios/Pydantic error shape into a single human-readable string. */
const extractError = (err, fallback = 'Failed to end trip.') => {
  const detail = err?.response?.data?.detail;
  if (!detail) return err?.message || fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    // FastAPI 422 validation errors
    return detail
      .map((d) => {
        if (typeof d === 'string') return d;
        const loc = Array.isArray(d?.loc) ? d.loc.filter((p) => p !== 'body').join('.') : '';
        return loc ? `${loc}: ${d?.msg || ''}` : d?.msg || JSON.stringify(d);
      })
      .join('; ');
  }
  if (typeof detail === 'object') return detail.msg || JSON.stringify(detail);
  return String(detail);
};

const PostTripModal = ({ load, onClose, onComplete }) => {
  const startOdo = load.start_odometer || 0;
  const hasStartOdo = startOdo > 0;
  const [odometer, setOdometer] = useState('');
  const [issues, setIssues] = useState('');
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const errorRef = useRef(null);

  // POD enforcement
  const [podDocs, setPodDocs] = useState([]);
  const [docsLoading, setDocsLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [noPodCertified, setNoPodCertified] = useState(false);
  const [noPodReason, setNoPodReason] = useState('');

  const showError = (msg) => {
    setError(msg);
    // Scroll the error into view so the user sees it on mobile.
    // 100ms gives React enough time to mount the {error && ...} block (50ms was
    // racy when other concurrent state updates batched with setError).
    setTimeout(() => {
      errorRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, 100);
  };

  const refreshDocs = async () => {
    try {
      const res = await axios.get(`${API}/loads/${load.id}/documents`, { withCredentials: true });
      setPodDocs((res.data || []).filter((d) => d.doc_kind === 'pod'));
    } catch (e) {
      setPodDocs([]);
    } finally {
      setDocsLoading(false);
    }
  };

  useEffect(() => {
    refreshDocs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load.id]);

  const handlePodUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setError('');
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('doc_kind', 'pod');
      await axios.post(`${API}/loads/${load.id}/documents`, fd, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await refreshDocs();
      setNoPodCertified(false);
      setNoPodReason('');
    } catch (err) {
      showError(extractError(err, 'Failed to upload POD.'));
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDeletePod = async (docId) => {
    if (!window.confirm('Remove this POD?')) return;
    try {
      await axios.delete(`${API}/load-documents/${docId}`, { withCredentials: true });
      await refreshDocs();
    } catch (err) {
      showError(extractError(err, 'Failed to delete POD.'));
    }
  };

  const hasPod = podDocs.length > 0;

  const handleSubmit = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setError('');

    // ---- Odometer is ALWAYS required ----
    const raw = (odometer ?? '').toString().trim();
    if (!raw) {
      showError('Please enter the ending odometer reading.');
      return;
    }
    const odoVal = parseFloat(raw);
    if (isNaN(odoVal)) {
      showError('Please enter a valid ending odometer reading (numbers only).');
      return;
    }
    if (odoVal < 0) {
      showError('Odometer cannot be negative.');
      return;
    }
    if (hasStartOdo && odoVal <= startOdo) {
      showError(
        `Ending odometer (${odoVal.toLocaleString()}) must be greater than starting odometer (${Math.round(startOdo).toLocaleString()}).`
      );
      return;
    }

    // ---- POD enforcement ----
    if (!hasPod) {
      if (!noPodCertified) {
        showError('Please upload a Proof of Delivery, or check the "No POD available" box and explain why.');
        return;
      }
      if (!noPodReason.trim() || noPodReason.trim().length < 5) {
        showError('Please provide a reason (at least 5 characters) explaining why no POD is available.');
        return;
      }
    }

    setSubmitting(true);
    try {
      await axios.post(
        `${API}/loads/${load.id}/end-trip`,
        {
          odometer: odoVal,
          issues: issues || null,
          notes: notes || null,
          pod_certified_no_proof: !hasPod && noPodCertified,
          no_pod_reason: !hasPod && noPodCertified ? noPodReason.trim() : null,
        },
        { withCredentials: true }
      );
      onComplete();
    } catch (err) {
      showError(extractError(err, 'Failed to end trip.'));
    } finally {
      setSubmitting(false);
    }
  };

  const milesDriven = odometer && hasStartOdo ? Math.max(0, parseFloat(odometer) - startOdo) : 0;

  return (
    <div className="fixed inset-0 bg-slate-900/60 z-50 flex items-center justify-center p-4" data-testid="post-trip-modal">
      <div className="bg-white rounded-md w-full max-w-lg max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200 sticky top-0 bg-white z-10">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-md flex items-center justify-center">
              <CheckCircle className="w-5 h-5 text-[#34C759]" />
            </div>
            <div>
              <h2 className="text-xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Work Sans, sans-serif' }}>Complete Trip</h2>
              <p className="text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Load {load.load_ref || load.id.slice(-6)}</p>
            </div>
          </div>
          <button data-testid="close-post-trip" onClick={onClose} className="p-1.5 rounded-md hover:bg-slate-100"><X className="w-4 h-4" /></button>
        </div>

        {/* Use noValidate + onSubmit so we drive ALL validation ourselves and never silently swallow a click. */}
        <form onSubmit={handleSubmit} noValidate className="p-6 space-y-4">
          {/* Top-of-form error banner — visible without scrolling on short mobile viewports. */}
          {error && (
            <div
              ref={errorRef}
              className="bg-red-50 border border-red-300 rounded-md p-3 text-sm text-red-900 font-medium flex items-start gap-2"
              data-testid="post-trip-error"
              role="alert"
              aria-live="assertive"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {hasStartOdo && (
            <div className="bg-slate-50 border border-slate-200 rounded-md p-3 flex items-center justify-between text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              <span className="text-slate-600">Start odometer</span>
              <span className="font-semibold text-slate-900" data-testid="post-trip-start-odo">{Math.round(startOdo).toLocaleString()} mi</span>
            </div>
          )}

          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Ending Odometer (mi) *
            </label>
            <div className="relative">
              <Gauge className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
              <input
                data-testid="post-trip-odometer"
                type="number"
                inputMode="numeric"
                step="1"
                value={odometer}
                onChange={(e) => setOdometer(e.target.value)}
                placeholder={hasStartOdo ? `Must be greater than ${Math.round(startOdo).toLocaleString()}` : 'Final mileage'}
                className="w-full pl-9 pr-3 py-2 border border-slate-200 rounded-md text-base font-semibold"
                style={{ fontFamily: 'Work Sans, sans-serif' }}
              />
            </div>
            {odometer && hasStartOdo && (
              <p className="text-xs text-slate-500 mt-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }} data-testid="miles-driven-preview">
                Miles driven this trip: <span className="font-semibold text-slate-800">{milesDriven.toLocaleString()} mi</span>
              </p>
            )}
          </div>

          {/* ---------- Proof of Delivery section ---------- */}
          <div className="border-t border-slate-100 pt-4" data-testid="pod-section">
            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-2" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              Proof of Delivery (POD) *
            </label>

            {docsLoading ? (
              <p className="text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Checking documents...</p>
            ) : hasPod ? (
              <div className="space-y-2" data-testid="pod-uploaded-list">
                {podDocs.map((d) => (
                  <div key={d.id} className="flex items-center justify-between gap-2 bg-green-50 border border-green-200 rounded-md px-3 py-2 text-sm" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                    <div className="flex items-center gap-2 min-w-0">
                      <FileText className="w-4 h-4 text-green-700 flex-shrink-0" />
                      <span className="truncate text-green-900 font-medium" title={d.file_name}>{d.file_name}</span>
                    </div>
                    <button type="button" onClick={() => handleDeletePod(d.id)} data-testid={`pod-delete-${d.id}`} className="text-red-600 hover:text-red-800 p-1 rounded" aria-label="Remove POD">
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
                <label className="inline-flex items-center gap-2 text-xs text-slate-600 cursor-pointer hover:text-slate-900" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  <Upload className="w-3.5 h-3.5" />
                  <span>Add another POD</span>
                  <input data-testid="pod-extra-upload" type="file" className="hidden" accept="application/pdf,image/jpeg,image/png,image/webp" capture="environment" onChange={handlePodUpload} disabled={uploading} />
                </label>
              </div>
            ) : (
              <div className="space-y-3">
                <label className="flex items-center justify-center gap-2 w-full px-4 py-3 border-2 border-dashed border-slate-300 rounded-md text-sm font-medium text-slate-700 hover:border-[#002FA7] hover:bg-blue-50 cursor-pointer transition-all" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  <Upload className="w-4 h-4" />
                  <span>{uploading ? 'Uploading...' : 'Upload POD (PDF, JPG, PNG)'}</span>
                  <input data-testid="pod-upload" type="file" className="hidden" accept="application/pdf,image/jpeg,image/png,image/webp" capture="environment" onChange={handlePodUpload} disabled={uploading} />
                </label>

                <div className="flex items-center gap-2 text-xs text-slate-500" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  <div className="flex-1 h-px bg-slate-200" />
                  <span>or</span>
                  <div className="flex-1 h-px bg-slate-200" />
                </div>

                <label className="flex items-start gap-2 cursor-pointer" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                  <input
                    data-testid="no-pod-checkbox"
                    type="checkbox"
                    checked={noPodCertified}
                    onChange={(e) => setNoPodCertified(e.target.checked)}
                    className="w-4 h-4 mt-0.5 accent-[#002FA7]"
                  />
                  <span className="text-sm text-slate-800">
                    I certify that <span className="font-semibold">no Proof of Delivery is available</span> for this load.
                  </span>
                </label>

                {noPodCertified && (
                  <div className="pl-6">
                    <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      Reason no POD is available *
                    </label>
                    <textarea
                      data-testid="no-pod-reason"
                      rows="2"
                      value={noPodReason}
                      onChange={(e) => setNoPodReason(e.target.value)}
                      placeholder="e.g., Receiver refused to sign, drop-and-hook with no signature, etc."
                      className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm"
                      style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
                    />
                  </div>
                )}

                {!noPodCertified && (
                  <div className="bg-amber-50 border border-amber-200 rounded-md p-2.5 flex items-start gap-2" data-testid="pod-required-warning">
                    <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0 mt-0.5" />
                    <p className="text-xs text-amber-900" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
                      A POD is required to complete this load. Upload one above, or check the box to certify none is available.
                    </p>
                  </div>
                )}
              </div>
            )}
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Any issues during the trip?</label>
            <textarea data-testid="post-trip-issues" rows="2" value={issues} onChange={(e) => setIssues(e.target.value)} placeholder="Mechanical issues, accidents, delays..." className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
          </div>
          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Delivery Notes</label>
            <textarea rows="2" value={notes} onChange={(e) => setNotes(e.target.value)} className="w-full px-3 py-2 border border-slate-200 rounded-md text-sm" />
          </div>

          {error && (
            <div
              className="bg-red-50 border border-red-300 rounded-md p-3 text-sm text-red-900 font-medium flex items-start gap-2"
              role="alert"
              aria-live="assertive"
              style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
            >
              <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <div className="flex items-center justify-end gap-3 pt-2 border-t border-slate-100">
            <button type="button" onClick={onClose} className="px-4 py-2 rounded-md text-sm font-semibold text-slate-700 hover:bg-slate-100" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>Cancel</button>
            <button data-testid="submit-post-trip" type="submit" disabled={submitting || uploading} className="px-5 py-2 bg-[#002FA7] text-white rounded-md text-sm font-semibold hover:bg-[#00247D] transition-all disabled:opacity-50" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
              {submitting ? 'Submitting...' : 'Mark as Delivered'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PostTripModal;
